import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flex, Popconfirm } from 'antd';
import { Fragment, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  cancelHandoverTicket,
  confirmHandoverTickets,
  printHandoverTicket
} from 'src/apis/office-ticket/handoverTicket.api';
import Close from 'src/assets/icons/close.png';
import Icon from 'src/components/v2/Icon';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import { OfficeTicketDetailContext } from 'src/contexts/officeTicketDetail.context';
import { useBasePath } from 'src/hooks/useBasePath';
import { ApprovalStatus, TicketStatus } from 'src/types/helpdesk/ticket.type';
import { showNotification } from 'src/utils/notification';
import BtnCustom from './BtnCustom';
import './btnHandleList.scss';

export type TicketResponses = {
  ticketId: number;
  step: number;
};

export type OpenTicketApprovalType = {
  isOpen: boolean;
  ticketResponses: TicketResponses[];
};

export type RequestType = {
  ticketResponses: TicketResponses[];
  action: ApprovalStatus;
  note?: string;
};

export interface OpenNoteType {
  isOpen?: boolean;
  ticketResponses: TicketResponses[];
  approvalStatus: ApprovalStatus;
}

type Props = {
  type: 'handoverTicket';
};

const BtnOfficeHandle = ({ type }: Props) => {
  const { ticketSelected, setTicketSelected, userInfo, setIsTicketDetailCollapsed } = useContext(AppContext);
  const { handoverTicket, setHandoverTicket } = useContext(OfficeTicketDetailContext);

  const basePath = useBasePath();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  //#region cancel ticket
  const cancelHandoverTicketMutation = useMutation({
    mutationFn: (id: number) => cancelHandoverTicket(id),
    onSuccess: (_, id) => {
      showNotification(`Hủy phiếu thành công - ${handoverTicket?.data.code}`, 'success');
      handleRefresh();
    }
  });

  //#endregion

  //#region print ticket
  const printHandoverTicketMutation = useMutation({
    mutationFn: (id: number) => printHandoverTicket(id),
    onSuccess: (data) => {
      showNotification(`In phiếu thành công - ${handoverTicket?.data.code}`, 'success');
      const url = window.URL.createObjectURL(
        new Blob([data.data], {
          type: `application/pdf;charset=UTF-8 `
        })
      );
      window.open(url, '_blank');
    }
  });

  //#endregion

  //#region confirm ticket

  const confirmHandoverTicketMutation = useMutation({
    mutationFn: (id: number) => confirmHandoverTickets(id),
    onSuccess: (data) => {
      showNotification(`Xác nhận phiếu thành công - ${data?.data}`, 'success');
      handleRefresh();
    }
  });
  //#endregion

  const handleCancel = (id: number) => {
    switch (type) {
      case 'handoverTicket':
        cancelHandoverTicketMutation.mutate(id);
        break;

      default:
        break;
    }
  };

  const handlePrint = (id: number) => {
    switch (type) {
      case 'handoverTicket':
        printHandoverTicketMutation.mutate(id);
        break;
      default:
        break;
    }
  };

  const handleConfirm = (id: number) => {
    switch (type) {
      case 'handoverTicket':
        confirmHandoverTicketMutation.mutate(id);
        break;

      default:
        break;
    }
  };

  //#region HANDLE OTHER
  const handleRefresh = () => {
    if (basePath?.includes(urlPath.handoverTicket.location)) {
      queryClient.invalidateQueries(['handoverTicketsV2']);
      queryClient.invalidateQueries(['handoverTicket']);
    }

    queryClient.invalidateQueries(['statusCount']);
    queryClient.invalidateQueries(['segmentedStatusCount']);
    setTicketSelected(undefined);
    setIsTicketDetailCollapsed(true);
    setHandoverTicket(undefined);
  };

  const handleCloseDetail = () => {
    setIsTicketDetailCollapsed(true);
    setTicketSelected(undefined);
  };
  //#endregion

  const handleRenderButton = () => {
    if (!ticketSelected) {
      return <Fragment />;
    }
    //#region render leave ticket
    // edit, cancel, print leave ticket
    if (basePath === urlPath.myHandoverTicket.location) {
      return (
        <Flex gap={10}>
          {handoverTicket?.data.createdById === userInfo?.id && handoverTicket?.data.isAvailableEdit && (
            <BtnCustom
              type='edit'
              onClick={() => navigate(`${urlPath.handoverTicketEdit.location}/${ticketSelected}`)}
            />
          )}
          {handoverTicket?.data.createdById === userInfo?.id && handoverTicket?.data.ticketStatusId === null && (
            <Popconfirm title={'Bạn chắc chắn hủy phiếu?'} onConfirm={() => handleCancel(handoverTicket.data?.id)}>
              <BtnCustom type='cancelTicket' />
            </Popconfirm>
          )}
          {handoverTicket?.data.createdById === userInfo?.id &&
            (handoverTicket?.data.ticketStatusId === null ||
              handoverTicket?.data.ticketStatusId === TicketStatus.Finished) && (
              <BtnCustom type='print' onClick={() => handlePrint(handoverTicket.data?.id)} />
            )}
        </Flex>
      );
    }

    // confirm
    if (basePath === urlPath.handoverTicketApproval.location && handoverTicket?.data.ticketStatusId === null) {
      return (
        <Flex gap={10}>
          {!handoverTicket.data.handOverTicketConfirms.find((confirm) => confirm.confirmerId === userInfo?.id) && (
            <Popconfirm title={'Bạn chắc chắn phê duyệt?'} onConfirm={() => handleConfirm(handoverTicket.data.id)}>
              <BtnCustom type='confirm' />
            </Popconfirm>
          )}
        </Flex>
      );
    }
    //#endregion
  };

  return (
    <Fragment>
      <Flex className='row-handle' justify='space-between' vertical={false}>
        <Icon url={Close} className='btn-close' size={25} onClick={() => handleCloseDetail()} />
        <div className='btn-handle'>{handleRenderButton()}</div>
      </Flex>
    </Fragment>
  );
};

export default BtnOfficeHandle;
